// src/Hooks/useNotifications.js
import { useState, useEffect, useContext } from "react";
import AuthContext from '../context/AuthContext';
import { db } from '../firebaseConfig'; // Import your new Firebase config
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  doc, 
  updateDoc, 
  writeBatch 
} from "firebase/firestore";

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true); // Add a loading state
  const { user } = useContext(AuthContext); // Only need the user ID


  // Effect to set up the real-time listener
  useEffect(() => {
    if (!user || !user.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    // 1. Create a query to get notifications for this user, ordered by date
    const q = query(
      collection(db, "notifications"),
      where("recipient", "==", user.id),
      orderBy("createdAt", "desc")
    );

    // 2. Set up the real-time listener
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fetchedNotifications = [];
      let count = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const notification = { 
          ...data, 
          _id: doc.id,
          createdAt: data.createdAt.toDate() 
        };
        fetchedNotifications.push(notification);
        if (!notification.isRead) {
          count++;
        }
      });

      setNotifications(fetchedNotifications);
      setUnreadCount(count);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to notifications:", error);
      setLoading(false);
    });

    // 3. Return the cleanup function
    return () => unsubscribe();
    
  }, [user]); // Re-run only when the user changes

  // Function to mark one notification as read
  const markOneAsRead = async (notificationId) => {
    const docRef = doc(db, "notifications", notificationId);
    try {
      await updateDoc(docRef, {
        isRead: true
      });
      // State will update automatically via the onSnapshot listener!
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Function to mark all as read
  const markAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      const batch = writeBatch(db);
      
      // Find all unread documents in the local state
      const unreadDocs = notifications.filter(n => !n.isRead);
      
      unreadDocs.forEach(notif => {
        const docRef = doc(db, "notifications", notif._id);
        batch.update(docRef, { isRead: true });
      });

      await batch.commit();
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };
  

  const refresh = () => {
    console.log("Notifications are live, no refresh needed.");
  };

  return {
    notifications,
    unreadCount,
    loading, // You can use this to show a spinner
    markAllAsRead,
    markOneAsRead,
    refresh
  };
};

export default useNotifications;